<template>
    <article v-sync-bodylock-gap
             class="top-header-container"
             :style="topHeaderStyle">
        <div class="top-header o-chrome-container h-full">
            <div class="flex items-center h-full relative">
                <CultureAwareRouterLink :to="frontpageUrl" :title="logoAltText">
                    <div :class="{ 'text-white': isInWhiteTheme, 'text-blue-500': !isInWhiteTheme }">
                        <template v-if="serverContextService.isOprema">
                            <CIcon name="oprema-logo" original width="150" class="block oprema-logo"/>
                        </template>
                        <template v-else-if="serverContextService.isHesa">
                            <CIcon name="logo-half-top" original width="40" class="block logo-half-top h-40"/>
                            <CIcon name="logo-half-eet" original width="40" class="block logo-half-eet"/>
                        </template>
                        <template v-else>
                            <CIcon name="logo-half-top" width="63" class="block logo-half-top"/>
                            <CIcon name="logo-half-eet" width="52" class="block logo-half-eet"/>
                        </template>
                    </div>
                </CultureAwareRouterLink>

                <div class="search-left-spacer"></div>

                <SearchCtrl class="search-field mr-auto text-black"
                            section-class="w-full"
                            :section-styling="{}"
                            :show-search-field="true"/>

                <div class="header-nav-buttons" :class="{ 'text-white': isInWhiteTheme }">
                    <div v-if="resourceMenu" class="header-nav-button">
                        <button type="button" class="my-account-link with-menu"
                                :class="{ 'text-white': isInWhiteTheme, 'with-menu__active': showResourcesMenu, 'theme-text-linkcolor': !isInWhiteTheme && showResourcesMenu }"
                                @click="handleClickToggleResourceMenu">
                            <CIcon name="resources"
                                   width="20"
                                   height="20"
                                   :fill="false"
                                   aria-hidden="true"/>
                            <span class="whitespace-nowrap font-medium">{{ $dictionary.get('FrontPage.Resources') }}</span>
                        </button>

                        <transition appear mode="out-in"
                                    enter-active-class="animated zoomIn u-anim-dur-250"
                                    leave-active-class="animated fadeOut u-anim-dur-150">
                            <TopHeaderResourceMenu v-if="showResourcesMenu" @close="closeResourcesMenu"/>
                        </transition>
                        <!-- SEO only - same links as above, but no v-if condition for showing -->
                        <nav class="hidden">
                            <div v-for="(linkGroup, linkGroupIndex) in resourceMenu.resourceMenuItemGroups"
                                 :key="linkGroupIndex">
                                <CultureAwareRouterLink v-for="(link, linkIndex) in linkGroup.links" :key="linkIndex"
                                                        :to="link.url"
                                                        :target="link.target">
                                    {{ link.name }}
                                </CultureAwareRouterLink>
                            </div>
                        </nav>
                    </div>
                    <div v-if="!resourceMenu && productGuidePage" class="header-nav-button">
                        <CultureAwareRouterLink class="my-account-link"
                                                :to="productGuidePage"
                                                :class="{ 'text-white': isInWhiteTheme }">
                            <CIcon name="productguide" width="20" height="20" aria-hidden="true"/>
                            <span class="whitespace-nowrap">{{ $dictionary.get('ProductGuide') }}</span>
                        </CultureAwareRouterLink>
                    </div>

                    <div v-if="salesAndSupportPage" class="header-nav-button">
                        <CultureAwareRouterLink class="my-account-link"
                                                :to="salesAndSupportPage"
                                                :class="{ 'text-white': isInWhiteTheme }">
                            <CIcon name="support" width="20" height="20" aria-hidden="true"/>
                            <span class="whitespace-nowrap">{{ $dictionary.get('FrontPage.SalesAndSupport') }}</span>
                        </CultureAwareRouterLink>
                    </div>

                    <template v-if="isLoggedIn">
                        <div v-if="$permission.canViewFavourites()"
                             class="header-nav-button">
                            <CultureAwareRouterLink class="my-account-link"
                                                    :to="{ name: favouritesPageName }"
                                                    :class="{ 'text-white': isInWhiteTheme }">
                                <CIcon name="favorite-outline" width="20" height="20" aria-hidden="true"/>
                                <span class="whitespace-nowrap">{{ $dictionary.get('MyAccount.Favourites.Title') }}</span>
                            </CultureAwareRouterLink>
                        </div>

                        <div v-if="$permission.canViewMyAccount()"
                             class="header-nav-button">
                            <CultureAwareRouterLink class="my-account-link"
                                                    :to="{ name: myAccountPageName }"
                                                    :class="{ 'text-white': isInWhiteTheme }">
                                <CIcon name="profile" width="20" height="20" aria-hidden="true"/>
                                <span class="whitespace-nowrap">{{ $dictionary.get('MyAccount.Header.Link') }}</span>
                            </CultureAwareRouterLink>
                        </div>
                    </template>

                    <button v-else class="header-nav-button" type="button" @click="login">
                        <span class="my-account-link font-bold">
                            <CIcon name="login" width="20" height="20" aria-hidden="true"/>
                            <span class="whitespace-nowrap">{{ $dictionary.get('Authentication.Login.LoginBtn') }}</span>
                        </span>
                    </button>

                    <CultureAwareRouterLink v-if="showBecomeACustomer"
                                            data-tracking-name="becomeACustomerPage"
                                            class="header-nav-button self-start bg-blue-500 oprema:bg-opremablue-500 hesa:bg-hesared-500 text-white px-20 py-13 rounded-30"
                                            :to="becomeACustomerPage">
                        <span class="whitespace-nowrap font-regular leading-none">{{ $dictionary.get('Information.BecomeACustomer.HeaderButton') }}</span>
                    </CultureAwareRouterLink>

                    <button v-if="isLoggedIn && isDevMode"
                            type="button"
                            class="header-nav-button"
                            @click="logout">
                        <span class="my-account-link font-bold">
                            <CIcon name="login" width="20" height="20" aria-hidden="true"/>
                            <span class="whitespace-nowrap">{{ $dictionary.get('MyAccount.Navigation.SignOut') }}</span>
                        </span>
                    </button>
                </div>

                <MiniBasket v-if="isLoggedIn && $permission.canPlaceOrder()" class="ml-30"/>
            </div>
        </div>
    </article>
</template>

<script setup lang="ts">
import { ref, computed, StyleValue } from 'vue';
import authService from '@/core/auth/auth.service';
import SearchCtrl from '@/project/search/SearchCtrl.vue';
import MiniBasket from '@/project/basket/MiniBasket.vue';
import { PageName } from '@/router/routes/constants';
import serverContextService from '@/core/serverContext.service';
import loginFormOverlayService from '@/project/modals/overlays/LoginFormOverlay.service';
import themeService from '@/project/app/themes/theme.service';
import { hexToRgbValues } from '@/core/color/color.service';
import { isDevMode as isDevModeFn } from '@/core/environmentService';
import { ResourceMenuViewModel } from '@/types/contentServerContract';
import TopHeaderResourceMenu from '@/project/layout/page-header/desktop/TopHeaderResourceMenu.vue';
import Api from '@/project/http/Api.service';
import permissionService from '@/project/auth/permission.service';

const showResourcesMenu = ref(false);

function login() {
    loginFormOverlayService.show('login');
}

const closeResourcesMenu = () => {
    showResourcesMenu.value = false;
}

function logout() {
    authService.logOff();
    Api.authentication.logOff();
}

function handleClickToggleResourceMenu() {
    showResourcesMenu.value = !showResourcesMenu.value;
}

const resourceMenu = computed<ResourceMenuViewModel | null>(() => {
    if (permissionService.canPunchOut()) {
        return null;
    }
    return serverContextService.resourceMenu;
});

const isLoggedIn = computed<boolean>(() => {
    return authService.isLoggedIn();
});

const myAccountPageName = computed(() => {
    return PageName.MY_ACCOUNT_PROFILE;
});

const favouritesPageName = computed(() => {
    return PageName.MY_ACCOUNT_FAVOURITES;
});

const productGuidePage = computed(() => {
    if (permissionService.canPunchOut()) {
        return undefined;
    }
    return serverContextService?.settings?.productGuidePage;
});

const becomeACustomerPage = computed(() => {
    return serverContextService?.settings?.becomeACustomerPage;
});

const showBecomeACustomer = computed<boolean>(() => {
    // Only show for users who has never been logged in - and only if page exists
    return ((!isLoggedIn.value && !authService.hasLoggedInBefore()) && !!becomeACustomerPage.value);
});

const salesAndSupportPage = computed(() => {
    if (permissionService.canPunchOut()) {
        return undefined;
    }
    return serverContextService?.settings?.salesAndSupportPage;
});

const isDevMode = computed<boolean>(() => {
    return isDevModeFn();
});

const topHeaderStyle = computed<StyleValue>(() => {
    const rgb = themeService.theme?.topColor && hexToRgbValues(themeService.theme.topColor);

    return {
        ...(rgb && { backgroundColor: `rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, ${themeService.takeOverOpacity})` }),
        ...(themeService.theme?.topColor && { borderColor: themeService.theme.topColor })
    };
});

const isInWhiteTheme = computed(() => {
    return themeService.isInWhiteTheme;
});

const logoAltText = computed<string>(() => {
    return serverContextService.footer?.companyDetails?.companyName || '';
});

const frontpageUrl = computed<string>(() => {
    return serverContextService.siteInfo?.url || '/';
});
</script>

<style lang="less" scoped>
.top-header-container {
    @apply h-full bg-white text-black;

    transition: background-color 500ms ease-in-out, color 500ms ease-in-out;
}

.logo-half-top,.logo-half-eet {
    transition: opacity 250ms ease-in-out, transform 250ms ease-in-out, background-color 500ms ease-in-out, color 500ms ease-in-out;
}

.oprema-logo {
    height: 5rem;
    padding-top: 0.6rem;
}

.top-header-fixed & {
    .logo-half-eet {
        transform: translateY(-15px);
        opacity: 0;
    }

    .logo-half-top {
        transform: translateY(11px);
    }
}

.search-field {
    max-width: 35rem;
    width: 100%;
}

.search-left-spacer {
    // Visually align center. Constants set from visually nudging.
    flex-grow: 0;
    flex-shrink: 1;
    flex-basis: calc(50vw - 255px);
    max-width: 49rem;
    body[data-theme="oprema"] & {
        min-width: 33rem;
    }
}

.header-nav-buttons {
    @apply flex flex-row items-center font-medium;

    .header-nav-button {
        @apply flex flex-col items-center justify-center mr-20;
        &.bg-blue-500 {
            // Add border / outline for use when takeover banner is active
            outline: 0.2rem solid currentColor;
        }

        &:last-child {
            @apply mr-0;
        }

        svg {
            @apply mb-2;
        }

        .my-account-link {
            @apply text-12 flex flex-col items-center justify-center text-center;

            @media (hover) {
                &:hover {
                    @apply underline;
                }
            }

            &.router-link-active {
                @apply font-bold underline;
            }
        }
    }
}
.c-page-header-resource-menu {
    a {
        &:focus,
        &:hover {
            span {
                @apply underline;
            }
        }
    }
}

@screen md {
    .header-nav-buttons {
        @apply ml-10;

        .my-account-link.with-menu {
            &:after {
                @apply absolute block bg-current bottom-0;
                content: "";
                height: 0.3rem;
                transition: transform 0.3s ease;
                transform: scaleX(0);
                width: 8rem;
            }

            &.with-menu__active:after, &:hover:after {
                transform: scaleX(1);
            }
        }
    }
}
</style>
